// Utility functions for consistent formatting across all weld tables

// Timezone conversion functions
const getTimezoneFromSettings = (): string => {
  try {
    const settings = localStorage.getItem('settings');
    if (settings) {
      const settingsData = JSON.parse(settings);
      return settingsData.general?.timeZone || 'utc';
    }
  } catch (error) {
  }
  return 'utc'; // Default fallback
};

// Convert timestamp to display timezone
const convertTimestampToTimezone = (timestamp: number | string): Date => {
  const timezone = getTimezoneFromSettings();
  const date = new Date(timestamp);
  
  switch (timezone) {
    case 'est':
      // Convert FROM UTC TO Eastern Time (UTC-5)
      return new Date(date.getTime() - (5 * 60 * 60 * 1000));
    case 'pst':
      // Convert FROM UTC TO Pacific Time (UTC-8)
      return new Date(date.getTime() - (8 * 60 * 60 * 1000));
    case 'utc':
    default:
      // Keep as UTC
      return date;
  }
};

// Get timezone display name
export const getTimezoneDisplayName = (): string => {
  const timezone = getTimezoneFromSettings();
  
  switch (timezone) {
    case 'est':
      return 'Eastern Time (EST)';
    case 'pst':
      return 'Pacific Time (PST)';
    case 'utc':
    default:
      return 'UTC';
  }
};

/**
 * Format time for display with timezone conversion (consistent with unitUtils.ts)
 * @param timeString - ISO string or timestamp
 * @returns Formatted time string like "25-Jan-2025, 2:30 PM"
 */
export const formatTimeForDisplay = (timeString?: string | number) => {
  if (!timeString) return '-';
  
  try {
    const convertedDate = convertTimestampToTimezone(timeString);
    const day = String(convertedDate.getDate()).padStart(2, '0');
    const month = convertedDate.toLocaleDateString('en-US', { month: 'short' });
    const year = convertedDate.getFullYear();
    const hours = convertedDate.getHours();
    const minutes = String(convertedDate.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${day}-${month}-${year}, ${displayHours}:${minutes} ${ampm}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format duration from minutes to "Xm Ys" format
 * @param durationString - Duration string like "2.56 mins" or "2.56"
 * @returns Formatted duration like "2m 17s"
 */
export const formatDuration = (durationString?: string | number) => {
  if (!durationString) return '-';
  
  try {
    let totalMinutes: number;
    
    if (typeof durationString === 'number') {
      totalMinutes = durationString;
    } else {
      // Extract minutes from "2.56 mins" format
      const minutesMatch = durationString.match(/(\d+\.?\d*)\s*mins?/);
      if (minutesMatch) {
        totalMinutes = parseFloat(minutesMatch[1]);
      } else {
        // Try to parse as direct number
        totalMinutes = parseFloat(durationString);
      }
    }
    
    if (isNaN(totalMinutes)) return durationString;
    
    const minutes = Math.floor(totalMinutes);
    const seconds = Math.round((totalMinutes - minutes) * 60);
    
    if (seconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${seconds}s`;
    }
  } catch (error) {
    return durationString;
  }
};

/**
 * Format timestamp for display with timezone conversion (consistent with unitUtils.ts)
 * @param timestamp - Unix timestamp in seconds
 * @returns Formatted time string like "25-Jan-2025, 2:30 PM"
 */
export const formatTimestamp = (timestamp?: number) => {
  if (!timestamp) return '-';
  // Convert Unix timestamp from seconds to milliseconds for formatTimeForDisplay
  return formatTimeForDisplay(timestamp * 1000);
};

/**
 * Format decimal numbers with consistent decimal places
 * @param value - Number to format
 * @param decimals - Number of decimal places
 * @returns Formatted number string
 */
export const formatDecimal = (value?: number | null, decimals: number = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return value.toFixed(decimals);
};

/**
 * Format time for display showing only time with seconds (timezone-aware)
 * @param timeString - ISO string or timestamp
 * @returns Formatted time string like "9:37:16 PM"
 */
export const formatTimeOnly = (timeString?: string | number) => {
  if (!timeString) return '-';
  
  try {
    const convertedDate = convertTimestampToTimezone(timeString);
    const hours = convertedDate.getHours();
    const minutes = String(convertedDate.getMinutes()).padStart(2, '0');
    const seconds = String(convertedDate.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes}:${seconds} ${ampm}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format date for display showing only date (timezone-aware)
 * @param timeString - ISO string or timestamp
 * @returns Formatted date string like "25-Jan-2025"
 */
export const formatDateOnly = (timeString?: string | number) => {
  if (!timeString) return '-';
  
  try {
    const convertedDate = convertTimestampToTimezone(timeString);
    const day = String(convertedDate.getDate()).padStart(2, '0');
    const month = convertedDate.toLocaleDateString('en-US', { month: 'short' });
    const year = convertedDate.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format date and time for display (timezone-aware)
 * @param timeString - ISO string or timestamp
 * @returns Formatted date and time string like "25-Jan-2025, 2:30:45 PM"
 */
export const formatDateTime = (timeString?: string | number) => {
  if (!timeString) return '-';
  
  try {
    const convertedDate = convertTimestampToTimezone(timeString);
    const day = String(convertedDate.getDate()).padStart(2, '0');
    const month = convertedDate.toLocaleDateString('en-US', { month: 'short' });
    const year = convertedDate.getFullYear();
    const hours = convertedDate.getHours();
    const minutes = String(convertedDate.getMinutes()).padStart(2, '0');
    const seconds = String(convertedDate.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${day}-${month}-${year}, ${displayHours}:${minutes}:${seconds} ${ampm}`;
  } catch (error) {
    return '-';
  }
}; 